import { useState, useEffect, useRef, useCallback } from 'react';
import { useAuth } from '../AuthContext';

// CONFIGURACIÓN CORRECTA (30 minutos total)
const INACTIVITY_TIMEOUT = 25 * 60 * 1000;  // 25 minutos (mostrar warning a los 25)
const WARNING_BEFORE = 5 * 60 * 1000;       // 5 minutos antes de expirar
// Total: 30 minutos de inactividad

const ACTIVITY_EVENTS = ['mousedown', 'mousemove', 'keypress', 'scroll', 'touchstart', 'click', 'wheel'];
const sessionChannel = new BroadcastChannel('session_channel');

export const useInactivityLogout = () => {
  const { logout, user, refreshUser } = useAuth();
  const [showWarning, setShowWarning] = useState(false);
  const [timeLeft, setTimeLeft] = useState(0);

  const inactivityTimer = useRef(null);
  const warningTimer = useRef(null);
  const countdownInterval = useRef(null);
  const showWarningRef = useRef(false);
  const isRefreshingRef = useRef(false);

  const clearAllTimers = useCallback(() => {
    if (inactivityTimer.current) {
      clearTimeout(inactivityTimer.current);
      inactivityTimer.current = null;
    }
    if (warningTimer.current) {
      clearTimeout(warningTimer.current);
      warningTimer.current = null;
    }
    if (countdownInterval.current) {
      clearInterval(countdownInterval.current);
      countdownInterval.current = null;
    }
  }, []);

  const resetWarningState = useCallback(() => {
    setShowWarning(false);
    setTimeLeft(0);
    showWarningRef.current = false;
    isRefreshingRef.current = false;
  }, []);

  const forceLogout = useCallback(() => {
    clearAllTimers();
    resetWarningState();
    logout(true);
  }, [logout, clearAllTimers, resetWarningState]);

  const startCountdown = useCallback(() => {
    if (showWarningRef.current) return;

    setShowWarning(true);
    showWarningRef.current = true;
    setTimeLeft(WARNING_BEFORE / 1000);

    countdownInterval.current = setInterval(() => {
      setTimeLeft(prev => {
        if (prev <= 1) {
          clearInterval(countdownInterval.current);
          countdownInterval.current = null;
          forceLogout();
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
  }, [forceLogout]);

  const refreshTokenSilently = useCallback(async () => {
    if (isRefreshingRef.current) return;
    isRefreshingRef.current = true;

    try {
      const res = await fetch('https://apiprov.conduto.com.ec/api/refresh', {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' }
      });

      if (res.ok) {
        const data = await res.json();
        if (data.access_token) {
          localStorage.setItem('token', data.access_token);
          localStorage.setItem('tokenTimestamp', Date.now().toString());

          if (data.usuario && refreshUser) {
            await refreshUser();
          }

          sessionChannel.postMessage({
            type: 'SESSION_RENEWED',
            timestamp: Date.now()
          });
        }
      } else {
        setTimeout(() => forceLogout(), 5000);
      }
    } catch (err) {
      console.error('Error refreshing token:', err);
    } finally {
      isRefreshingRef.current = false;
    }
  }, [forceLogout, refreshUser]);

  const resetTimer = useCallback(() => {
    if (showWarningRef.current) return;
    if (isRefreshingRef.current) return;

    clearAllTimers();
    resetWarningState();

    try {
      localStorage.setItem('lastActivity', Date.now().toString());
    } catch (e) { }

    inactivityTimer.current = setTimeout(() => {
      startCountdown();
    }, INACTIVITY_TIMEOUT);

    warningTimer.current = setTimeout(() => {
      if (!showWarningRef.current) {
        refreshTokenSilently();
      }
    }, 20 * 60 * 1000);
  }, [clearAllTimers, resetWarningState, startCountdown, refreshTokenSilently]);

  const stayLoggedIn = useCallback(async () => {
    if (isRefreshingRef.current) return;

    try {
      const res = await fetch('https://apiprov.conduto.com.ec/api/refresh', {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' }
      });

      if (res.ok) {
        const data = await res.json();

        if (data.access_token) {
          localStorage.setItem('token', data.access_token);
          localStorage.setItem('tokenTimestamp', Date.now().toString());

          if (refreshUser) {
            await refreshUser();
          }

          clearAllTimers();
          resetWarningState();

          setTimeout(() => {
            resetTimer();
          }, 100);

          sessionChannel.postMessage({
            type: 'SESSION_RENEWED',
            timestamp: Date.now(),
            message: 'Token renovado'
          });
        } else {
          forceLogout();
        }
      } else {
        forceLogout();
      }
    } catch (err) {
      console.error('Error en stayLoggedIn:', err);
      forceLogout();
    }
  }, [forceLogout, clearAllTimers, resetWarningState, resetTimer, refreshUser]);

  useEffect(() => {
    if (!user) {
      clearAllTimers();
      resetWarningState();
    } else {
      clearAllTimers();
      resetWarningState();
      setTimeout(() => resetTimer(), 500);
    }
  }, [user, clearAllTimers, resetWarningState, resetTimer]);

  useEffect(() => {
    const handleActivity = () => {
      if (!showWarningRef.current && user) {
        try {
          localStorage.setItem('lastActivity', Date.now().toString());
        } catch (e) { }
        resetTimer();
      }
    };

    const handleForceExpired = () => forceLogout();
    window.addEventListener('force-session-expired', handleForceExpired);

    const handleSessionMessage = (event) => {
      if (event.data?.type === 'SESSION_RENEWED' && user) {
        clearAllTimers();
        resetWarningState();
        setTimeout(() => resetTimer(), 0);
      }
    };
    sessionChannel.addEventListener('message', handleSessionMessage);

    ACTIVITY_EVENTS.forEach(event => window.addEventListener(event, handleActivity));

    const handleVisibility = () => {
      if (document.visibilityState === 'visible' && user) {
        let last = 0;
        try {
          last = parseInt(localStorage.getItem('lastActivity') || '0', 10);
        } catch (e) { }

        const now = Date.now();
        const inactiveTime = now - last;

        if (inactiveTime > INACTIVITY_TIMEOUT + WARNING_BEFORE) {
          forceLogout();
        } else {
          resetTimer();
        }
      }
    };
    document.addEventListener('visibilitychange', handleVisibility);

    return () => {
      ACTIVITY_EVENTS.forEach(event => window.removeEventListener(event, handleActivity));
      window.removeEventListener('force-session-expired', handleForceExpired);
      sessionChannel.removeEventListener('message', handleSessionMessage);
      document.removeEventListener('visibilitychange', handleVisibility);
      clearAllTimers();
    };
  }, [user, resetTimer, forceLogout, clearAllTimers, resetWarningState]);

  return {
    showWarning,
    timeLeft,
    stayLoggedIn,
    handleLogout: () => logout(false),
    onSuccessfulRequest: resetTimer
  };
};